import { cloneDeep } from 'lodash';

import bus from '@/utils/bus';
import { ON_SITE_ORDER_ITEM_INDEX } from '@/utils/constants';
import { generateUniqueId } from '@/utils/helper';

import AddMenuOption from '../AddMenuOption/AddMenuOption.vue';
import MenuItemsEditorCategoryEntrees from '../MenuItemsEditorCategoryEntrees/MenuItemsEditorCategoryEntrees.vue';
import MenuItemsEditorEntreeDetails from '../MenuItemsEditorEntreeDetails/MenuItemsEditorEntreeDetails.vue';

export default {
  name: 'MenuItemsEditor',
  components: {
    AddMenuOption,
    MenuItemsEditorCategoryEntrees,
    MenuItemsEditorEntreeDetails,
  },
  props: {
    categories: {
      required: true,
      type: Array,
    },
    errorCategoriesMessages: {
      default: () => ([]),
      type: Array,
    },
    inEdit: {
      default: false,
      type: Boolean,
    },
    menu: {
      required: true,
      type: Object,
    },
    showAddCategoryBtn: {
      default: false,
      type: Boolean,
    },
  },
  data() {
    return {
      displayEntree: null,
      dragging: 0,
      isAddOptionMode: false,
    };
  },
  beforeDestroy() {
    this.displayEntree = null;
    this.dragging = 0;
    this.isAddOptionMode = false;
  },
  async created() {
    bus.$on('ADD_NEW_CATEGORY_WITH_ITEMS', (items = []) => {
      this.onAddCategory('品項類別');
      const lastCategory = this.menu.categories?.[this.menu.categories?.length - 1];
      items.forEach((item, _viewIndex) => {
        this.addNewEntree(item, lastCategory, _viewIndex);
      });
    });

    bus.$on('RESET_DISPLAY_ENTREE', () => {
      this.resetDisplayEntree();
    });

    bus.$on('REMOVE_MENU_OPTION_ITEM', (item) => {
      this.menu.removeItem(item);
    });

    bus.$on('CREATE_OPTION_ITEMS_AND_ASSIGN_TO_OPTION', ({ items = [], option }) => {
      this.createItemsAndAssignToOption(items, option);
    });

    bus.$on('SET_DISPLAY_ENTREE_WITH_ENTREE_INDEX', (entreeIndex) => {
      this.onShowEntreeDetails(entreeIndex);
    });

    if (!this.inEdit) {
      this.onAddCategory();
      const category = this.menu.categories?.[0];
      const categoryIndex = category?.index;
      this.onAddEntree(categoryIndex);
    }
  },
  mounted() {
    const dragBar = document.getElementById('drag-bar');
    dragBar.addEventListener('mousedown', () => {
      document.addEventListener('mousemove', this.resize);
      document.addEventListener('selectstart', this.disableSelect);
      document.addEventListener('mouseup', () => {
        document.removeEventListener('mousemove', this.resize);
        document.removeEventListener('selectstart', this.disableSelect);
      });
    });
  },
  methods: {
    addNewEntree(entree, specifiedCategory, _viewIndex) {
      const categoryIndex = specifiedCategory.index;
      const {
        name = '', note = '', price = '', optionIndexes = [], options = [], photo = '',
      } = entree;
      const entreeIndex = generateUniqueId() + _viewIndex;
      const newEntree = this.menu.newEntree();
      newEntree
        .setCategoryIndex(categoryIndex)
        .setIndex(entreeIndex)
        .setName(name)
        .setOptionIndexes(optionIndexes)
        .setPhoto(photo)
        .setPrice(price)
        .setStoreNote(note);
      options.forEach((ele) => {
        newEntree.addOption(ele);
      });
      specifiedCategory.addEntree(newEntree, _viewIndex);
      newEntree.setCategory(specifiedCategory);
      this.menu.addEntree(newEntree, _viewIndex);
      this.displayEntree = newEntree;
    },
    addOptionToCurrentEntree(option) {
      if (!option) {
        return;
      }
      this.displayEntree.addOption(option);
    },
    clearJSEvents() {
      this.dragging = 0;
      const { body } = document;
      body.removeEventListener('mousemove', this.resize);
      body.classList.remove('resizing');
    },
    createItemsAndAssignToOption(items = [], option) {
      if (!option) {
        throw new TypeError('Option is required for assign option items.');
      }
      const optionItems = this.createAndGetOptionItems(items);
      optionItems.forEach((item) => {
        option.addItem(item);
        item.addOption(option);
      });
    },
    createAndGetOptionItems(items = []) {
      if (!items?.length) {
        return [];
      }

      return items?.map(({ index, name, price = 0 }) => {
        const optionItem = this.menu.newItem();
        optionItem
          .setIndex(index)
          .setName(name)
          .setPrice(price);

        this.menu.addItem(optionItem);
        return optionItem;
      }) || [];
    },
    disableSelect(event) {
      event.preventDefault();
    },
    getCategoryEntrees(categoryIndex) {
      if (!categoryIndex) {
        return [];
      }
      const { entrees = [] } = this.menu?.getCategory(categoryIndex) || {};
      return entrees;
    },
    isNotOnSiteOrderCategory(index) {
      return index !== ON_SITE_ORDER_ITEM_INDEX;
    },
    onAddCategory(categoryName = '') {
      const index = generateUniqueId();
      const category = this.menu.newCategory();
      category.setIndex(index).setName(categoryName);
      this.menu.addCategory(category);
    },
    onAddEntree(categoryIndex) {
      if (!Number.isInteger(categoryIndex)) {
        throw new TypeError('categoryIndex is required for adding new entree.');
      }
      const index = generateUniqueId();
      const entree = this.menu.newEntree();
      entree
        .setCategoryIndex(categoryIndex)
        .setIndex(index)
        .setName('')
        .setOptionIndexes([])
        .setPhoto('')
        .setPrice(null)
        .setStoreNote('');

      const category = this.menu.getCategory(categoryIndex);
      category.addEntree(entree);
      entree.setCategory(category);
      this.menu.addEntree(entree);
      this.displayEntree = entree;
    },
    onAddOption(option) {
      if (!option) {
        throw new TypeError('Option is required for adding a new option');
      }

      const index = generateUniqueId();
      const {
        items = [],
        name,
        note = '',
        price,
        rules = {},
      } = option || {};

      const newOption = this.menu.newOption();
      newOption
        .setIndex(index)
        .setName(name)
        .setPrice(price)
        .setStoreNote(note)
        .setRules(rules);

      this.menu.addOption(newOption);

      this.createItemsAndAssignToOption(items, newOption);

      this.addOptionToCurrentEntree(newOption);
      this.onToggleOptionMode(false);
    },
    onShowEntreeDetails(entreeIndex) {
      if (!entreeIndex) {
        throw new TypeError('entreeIndex is required for showing entree details.');
      }
      this.displayEntree = this.menu.getEntree(entreeIndex);
    },
    onToggleOptionMode(mode = false) {
      this.isAddOptionMode = mode;
    },
    removeCategory(category) {
      if (!category) {
        return;
      }

      this.menu.removeCategory(category);
    },
    removeCategoryWithEntrees(category) {
      if (!category) {
        return;
      }
      const { entrees = [] } = category || {};
      const clonedEntrees = cloneDeep(entrees);
      if (clonedEntrees?.length) {
        clonedEntrees.forEach((entree) => {
          this.removeEntreeWithCategory(category, entree);
        });
      }
      this.removeCategory(category);
      this.resetDisplayEntree();
    },
    async removeEntree(entree = {}) {
      if (!entree) {
        return;
      }
      const entreeInfo = await this.menu.getEntree(entree.index);
      const category = this.menu.getCategory(entreeInfo.categoryIndex);
      if (category) {
        this.removeEntreeWithCategory(category, entree);
        if (this.displayEntree?.index === entree?.index) {
          this.resetDisplayEntree();
        }
      }
    },
    copyEntree(data) {
      const { id, viewIndex, categoryIndex } = data;
      const entree = this.menu.getEntree(id);
      const category = this.menu.getCategory(categoryIndex);
      this.addNewEntree(entree, category, viewIndex + 1);
    },
    removeEntreeWithCategory(category = {}, entree = {}) {
      if (!entree || !category) {
        return;
      }
      category.removeEntree(entree);
      this.menu.removeEntree(entree);
    },
    resetDisplayEntree() {
      this.displayEntree = null;
    },
    resize(e) {
      const leftMenu = document.getElementById('left-menu');
      const menuLeft = leftMenu.getBoundingClientRect().left;
      leftMenu.style.width = `${e.x - menuLeft}px`;
    },
  },
};
